import { useEffect, useState } from 'react';
import axios from 'axios';
import { parsearMontoCLP } from '../utils/montoCLP';

const FORMATO_FECHA = /^(\d{4})-(\d{2})-(\d{2})$/;

// Además de la forma, exige una fecha de calendario real. Se arma por componentes: new Date('AAAA-MM-DD')
// se interpreta en UTC y correría el día en Santiago.
function esFechaReal(texto) {
  const m = FORMATO_FECHA.exec(texto || '');
  if (!m) return false;
  const [anio, mes, dia] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const f = new Date(anio, mes - 1, dia);
  return f.getFullYear() === anio && f.getMonth() === mes - 1 && f.getDate() === dia;
}

// "AAAA-MM-DD" de hoy en Santiago (el servidor usa la misma zona para decidir qué está vigente).
// Se compone desde las partes para no depender del formato que cada navegador da a un locale.
export function hoyEnSantiago() {
  const partes = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Santiago', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date());
  const valor = (tipo) => partes.find((p) => p.type === tipo).value;
  return `${valor('year')}-${valor('month')}-${valor('day')}`;
}

const SIN_MONTO = 'No pudimos leer el monto del plan.';

// Sin plan_id en la URL (o sin backend) no hay nada que pedir: el monto sale del entorno desde el primer render.
const estadoSinRed = (planId) => ({
  monto: planId ? parsearMontoCLP(process.env.REACT_APP_DEFAULT_SUBSCRIPTION_AMOUNT) : null,
  cargando: false,
  error: '',
});

// Plan, fecha de inicio y monto de la suscripción a partir de la URL de /subscribe y /subscribe/pago:
// `plan_id` (si no viene, el plan del entorno), `start` (si no viene o está mal formado, hoy) y el
// monto real que Flow tiene para ese plan (solo cuando la URL trae plan_id; si no, el del entorno).
export default function usePlan(search) {
  const params = new URLSearchParams(search);
  const planIdUrl = params.get('plan_id');
  const planId = planIdUrl || process.env.REACT_APP_DEFAULT_PLAN_ID || '';
  const startUrl = params.get('start');
  const inicio = esFechaReal(startUrl) ? startUrl : hoyEnSantiago();

  const [estado, setEstado] = useState(() => (
    planId && planIdUrl && process.env.REACT_APP_API_URL
      ? { monto: null, cargando: true, error: '' }
      : estadoSinRed(planId)
  ));

  useEffect(() => {
    const API_URL = process.env.REACT_APP_API_URL;
    if (!(planId && API_URL && planIdUrl)) {
      setEstado(estadoSinRed(planId));
      return undefined;
    }

    let cancelado = false;
    setEstado({ monto: null, cargando: true, error: '' });
    axios.get(`${API_URL}/api/subscription/plan`, { params: { planId } })
      .then((resp) => {
        if (cancelado) return;
        const monto = parsearMontoCLP(resp.data?.amount);
        setEstado(monto
          ? { monto, cargando: false, error: '' }
          : { monto: null, cargando: false, error: SIN_MONTO });
      })
      .catch((err) => {
        if (cancelado) return;
        console.error('Error obteniendo plan:', err);
        setEstado({ monto: null, cargando: false, error: err.response?.data?.message || SIN_MONTO });
      });
    return () => { cancelado = true; };
  }, [planId, planIdUrl]);

  return { planId, planIdUrl, inicio, ...estado, faltaPlan: !planId };
}
